import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Check, MessageCircle, Phone, CalendarClock, ArrowUpRight } from "lucide-react";

import { useSeo } from "@/lib/seo";
import { CONTACT, waLink, mailtoLink } from "@/data/site";
import { LOCATIONS } from "@/data/locations";
import { PageHeader } from "@/components/site/PageHeader";
import { SSButton } from "@/components/site/SSButton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const CHANNELS = [
  {
    icon: MessageCircle,
    title: "WhatsApp",
    text: "The quickest way to reach Suhail directly.",
    label: "Message on WhatsApp",
    href: () => waLink("Hi Suhail, I'd like to discuss a property decision in Uttarakhand."),
  },
  {
    icon: Phone,
    title: "Request a callback",
    text: "Leave your number and a convenient time.",
    label: "Request a callback",
    scrollTo: "enquiry-form",
  },
  {
    icon: CalendarClock,
    title: "Book an advisory conversation",
    text: "A focused, buyer-side discussion of your decision.",
    label: "Request a conversation",
    scrollTo: "enquiry-form",
  },
];

export default function Advisory() {
  const [params] = useSearchParams();
  const prefillLocation = params.get("location") || "";
  const prefillContext = params.get("context") || "";

  useSeo({
    title: "Advisory",
    description:
      "Request an independent, buyer-side property assessment for Uttarakhand. Reach Suhail by WhatsApp, callback, advisory conversation or enquiry form.",
    path: "/advisory",
  });

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    location: prefillLocation,
    subject: prefillContext ? "Guided intelligence enquiry" : "",
    message: prefillContext || "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email.";
    if (!form.message.trim()) e.message = "Please tell us a little about what you're considering.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const response = await fetch("/.netlify/functions/enquiry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, source: "advisory" }),
      });
      if (!response.ok) throw new Error("Enquiry email delivery failed");
      setDone(true);
      toast.success("Enquiry received", { description: "Thank you — we'll be in touch shortly." });
    } catch (err) {
      toast.error("Something went wrong", { description: "Please try again, or reach us on WhatsApp." });
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToForm = () => document.getElementById("enquiry-form")?.scrollIntoView({ behavior: "smooth" });

  return (
    <>
      <PageHeader
        eyebrow="Advisory"
        marker="SS·ADV"
        title="Discuss a property decision"
        intro="There is no cost to an initial conversation and no obligation. Tell us what you're weighing, and we'll help you see it clearly — independently, on the buyer's side."
        breadcrumbs={[{ label: "Advisory" }]}
      />

      {/* Channels */}
      <section className="ss-container pb-16">
        <div className="grid md:grid-cols-3 gap-5 lg:gap-7">
          {CHANNELS.map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.title} className="border border-[#E2DDD5] bg-[#F2EFE9] p-8 flex flex-col" data-testid={`channel-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <Icon className="h-6 w-6 text-[#2D3B32]" strokeWidth={1.4} />
                <h2 className="mt-5 font-display text-2xl font-normal text-[#1A1C1E]">{c.title}</h2>
                <p className="mt-2 text-[0.95rem] font-light text-[#52575D] flex-1">{c.text}</p>
                {c.href ? (
                  <a href={c.href()} target="_blank" rel="noopener noreferrer" className="group mt-6 inline-flex items-center gap-1.5 text-[0.76rem] uppercase tracking-[0.1em] text-[#1A1C1E]">
                    <span className="ss-link">{c.label}</span>
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
                  </a>
                ) : (
                  <button onClick={scrollToForm} className="group mt-6 inline-flex items-center gap-1.5 text-[0.76rem] uppercase tracking-[0.1em] text-[#1A1C1E]">
                    <span className="ss-link">{c.label}</span>
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Form */}
      <section id="enquiry-form" className="scroll-mt-28 bg-[#EFECE6] py-20 lg:py-28" data-testid="enquiry-section">
        <div className="ss-container grid lg:grid-cols-[0.8fr_1.2fr] gap-12 lg:gap-20">
          <div>
            <p className="ss-overline text-[#8C6A53] mb-5">Enquiry</p>
            <h2 className="font-display text-4xl sm:text-5xl font-light text-[#1A1C1E] leading-tight">Request a buyer-side assessment</h2>
            <p className="mt-6 text-[1.02rem] font-light text-[#52575D] leading-relaxed">
              Share as much or as little as you like. We'll respond with a considered, independent view — not a sales pitch.
            </p>
            <div className="mt-8 space-y-2 text-[0.9rem] font-light text-[#52575D]">
              <p><span className="font-mono-ss text-[0.66rem] uppercase tracking-wider text-[#8A9098] mr-3">Email</span><a href={mailtoLink()} className="ss-link">{CONTACT.email}</a></p>
              <p><span className="font-mono-ss text-[0.66rem] uppercase tracking-wider text-[#8A9098] mr-3">WhatsApp</span><a href={waLink()} target="_blank" rel="noopener noreferrer" className="ss-link">{CONTACT.whatsappDisplay}</a></p>
              <p><span className="font-mono-ss text-[0.66rem] uppercase tracking-wider text-[#8A9098] mr-3">Phone</span><a href={`tel:+${CONTACT.whatsapp}`} className="ss-link">{CONTACT.whatsappDisplay}</a></p>
            </div>
          </div>

          <div>
            <AnimatePresence mode="wait">
              {done ? (
                <motion.div
                  key="done"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="border border-[#2D3B32]/30 bg-[#F7F5F0] p-10 text-center"
                  data-testid="enquiry-success"
                >
                  <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#2D3B32] text-[#F7F5F0]">
                    <Check strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-3xl font-light text-[#1A1C1E]">Enquiry received</h3>
                  <p className="mt-3 text-[0.98rem] font-light text-[#52575D]">
                    Thank you for reaching out. We'll be in touch at {form.email} shortly. For anything time-sensitive, message us on WhatsApp.
                  </p>
                  <div className="mt-7">
                    <SSButton href={waLink(`Hi Suhail, I've just submitted an enquiry (${form.name}).`)} variant="primary" arrow={false}>
                      Continue on WhatsApp
                    </SSButton>
                  </div>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  onSubmit={onSubmit}
                  noValidate
                  className="space-y-6"
                  data-testid="enquiry-form"
                >
                  <div className="grid sm:grid-cols-2 gap-6">
                    <Field label="Name" error={errors.name}>
                      <Input value={form.name} onChange={set("name")} placeholder="Your name" data-testid="enquiry-name" className="ss-field" />
                    </Field>
                    <Field label="Email" error={errors.email}>
                      <Input type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" data-testid="enquiry-email" className="ss-field" />
                    </Field>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <Field label="Phone (optional)">
                      <Input value={form.phone} onChange={set("phone")} placeholder="For a callback" data-testid="enquiry-phone" className="ss-field" />
                    </Field>
                    <Field label="Location of interest (optional)">
                      <select value={form.location} onChange={set("location")} data-testid="enquiry-location" className="ss-field h-11 w-full bg-transparent border border-[#C8C2B7] px-3 text-[0.95rem] text-[#1A1C1E] focus:outline-none focus:border-[#1A1C1E]">
                        <option value="">Select a location</option>
                        {LOCATIONS.map((l) => <option key={l.slug} value={l.name}>{l.name}</option>)}
                        <option value="Elsewhere in Uttarakhand">Elsewhere in Uttarakhand</option>
                        <option value="Still exploring">Still exploring</option>
                      </select>
                    </Field>
                  </div>
                  <Field label="What are you considering?" error={errors.message}>
                    <Textarea value={form.message} onChange={set("message")} rows={5} placeholder="Tell us about the decision you're weighing — a location, a plot, a question about land or law…" data-testid="enquiry-message" className="ss-field resize-none" />
                  </Field>
                  <div className="flex flex-wrap items-center gap-4 pt-2">
                    <SSButton type="submit" variant="primary" arrow={false} disabled={submitting} data-testid="enquiry-submit" className="disabled:opacity-60">
                      {submitting ? "Sending…" : "Send enquiry"}
                    </SSButton>
                    <p className="text-[0.76rem] font-light text-[#8A9098] max-w-xs">
                      Your details are used only to respond to your enquiry. We never share them.
                    </p>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>
    </>
  );
}

const Field = ({ label, error, children }) => (
  <div>
    <Label className="ss-overline text-[#8A9098] mb-2 block">{label}</Label>
    {children}
    {error && <p className="mt-1.5 text-[0.78rem] text-[#8C2E2E]" role="alert">{error}</p>}
  </div>
);
